/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Component } from '@appCanvasCommon/component-system/Component';
import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { EventBinder } from '@appPlanApplication/interaction';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { DisplayObject } from 'pixi.js-legacy';
import { IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { CursorType } from '@appCanvasCommon/events/cursor-type.enum';
import { HammerEvent } from '@appCanvasCommon/interaction/EventBinder';
import { ActiveLine } from '../../../../app-data/models/ActiveLine';
import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { IActiveColumn } from '../../../../modules/plan/services/active-column.service';
import { DragAtEdge } from '../../../../app-data/models/DragAtEdge';
import { PromisePeriod } from '../../../../app-data/models/PromisePeriod';
import { TimelineDisplayItem } from '@appPlanApplication/timeline/tickets/TimelineDisplayItem';
import { ITimelinePromisePeriod, PeriodDragDirection } from '@appPlanApplication/timeline/TimelineHelpers';
import { snapping } from 'utils';
import { dateCache } from '@touchplan/common';
import { RenderType } from 'unitTestUtils/SplitDataMock';
import { analyticsEventsService } from '../../../services/analytics/AnalyticsEventsService';
/**
 * Magic numbers and other settings related to active line drag logic
 */
const settings = {
	snapStickiness: 500, // Magic number that represents how sticky the drag operation is (500 comes from old code)
};
/**
 * PromisePeriodDrag
 * Component which handles all of the drag operations for the next Promise period
 */
export class PromisePeriodDrag extends Component implements ComponentInterface {
	/**
	 * Instance of EventBinder, which connects HammerJS events to the PixiJS event system.
	 * @private
	 */
	private _binder!: EventBinder | null;
	/**
	 * We store the last ActiveLine so we can synchronously access data while performing drag calculations
	 * @private
	 */
	private _cachedActiveLine: ActiveLine | null = null;
	/**
	 * We store the last Promise Period List so we can synchronously access data while performing drag calculations
	 * @private
	 */
	private _cachedPromisePeriodList!: Map<string, PromisePeriod> | null;
	/**
	 * We store the current active columns so we can synchronously access column data when performing drag calculations
	 * @private
	 */
	private _cachedActiveColumns!: IActiveColumn[] | null;
	/**
	 * Cleans up any internal subscriptions when this component is destroyed
	 * @protected
	 */
	private _shutdownSubject$: Subject<void> | null = new Subject<void>();
	/**
	 * Instance of our DragAtEdge helper which facilitates detecting when we are at the edge of the screen
	 * @private
	 */
	private _dragAtEdge!: DragAtEdge | null;
	/**
	 * We need to create a new DragAtEdge for each dragStart event in case the screen size changes in between drags
	 * This array is to track subscriptions so we can properly cleanup all of our subscribers once a drag ends.
	 * @private
	 */
	private _dragCleanup: Subscription[] = [];
	/**
	 * The starting X of the drag
	 * @private
	 */
	private _startX!: number;
	/**
	 * The ending X of the drag
	 * @private
	 */
	private _endX!: number;
	/**
	 * The TimelineDisplayItem attached to this component. We set this at the start of a drag
	 * @private
	 */
	private _timelineItem!: TimelineDisplayItem<ITimelinePromisePeriod>;
	/**
	 * The last active column we matched from the snapping function
	 * @private
	 */
	private _lastSnapColumn!: IActiveColumn | undefined;
	/**
	 * The setup method is called once all properties on a component have been decorated by the ComponentManager.
	 * From this point on, you can safely use this.getParent() and this.getManager().
	 * Arguments passed into setup are passed in via the ComponentManager.add(name, component, ...args)
	 * @param canvasAngularLand - Dependency object for Canvas / Angular connection
	 * @param parent - The display object that this component attaches to
	 * @param ghostLine - The ghost line that appears when we start dragging
	 * @param	label - We pass in the label of the active line to access the exact text.
	 */
	public override setup(canvasAngularLand: CanvasAngularLand, parent: DisplayObject): void {
		this.canvasAngularLand = canvasAngularLand;
		this._binder = new EventBinder(parent);
		parent.eventMode = EventMode.Static;
		parent.cursor = CursorType.Grab;

		this.canvasAngularLand.activeLineService.changed$
			.pipe(takeUntil(this._shutdownSubject$ as Subject<void>))
			.subscribe((activeLine: ActiveLine | null) => {
				this._cachedActiveLine = activeLine;
			});

		this.canvasAngularLand.activeColumnService.visibleColumns$
			.pipe(takeUntil(this._shutdownSubject$ as Subject<void>))
			.subscribe((columns: IActiveColumn[] | null) => (this._cachedActiveColumns = columns));

		this.canvasAngularLand.promisePeriodService.changed$
			.pipe(takeUntil(this._shutdownSubject$ as Subject<void>))
			.subscribe((promisePeriod: Map<string, PromisePeriod> | null) => {
				this._cachedPromisePeriodList = promisePeriod;
			});

		this._binder.add('hammer-panstart', this.dragStart.bind(this));
		this._binder.add('hammer-pan', this.drag.bind(this));
		this._binder.add('hammer-panend', this.dragEnd.bind(this));
		this._binder.add('hammer-holddragstart', this.dragStart.bind(this));
		this._binder.add('hammer-holddrag', this.drag.bind(this));
		this._binder.add('hammer-holddragend', this.dragEnd.bind(this));
	}

	/**
	 * Handles the start of a drag operation
	 */
	public dragStart(_: HammerEvent): void {
		if (!this.canDrag()) {
			return;
		}

		this.canvasAngularLand.promisePeriodService.switchViewToEdit();
		// We are creating a new subscription on drag start instead of in setup so we have the most accurate
		// camera data. Setting up in setup can result in stale camera data otherwise.
		this._dragAtEdge = new DragAtEdge(this.canvasAngularLand.cameraService.screen);
		const edgeSubscription: Subscription = this._dragAtEdge.change$.subscribe((event) => {
			// Forcing deltaY to 0 because we only want to pan along the X-axis
			this.canvasAngularLand.cameraService.translate(-event.deltaX, 0);
		});

		this._dragCleanup.push(edgeSubscription);

		this._timelineItem = this.getParent() as TimelineDisplayItem<ITimelinePromisePeriod>;
		this._startX = this._timelineItem.data.spatialData.startWorldX;
		this._endX = this._timelineItem.data.spatialData.endWorldX;
	}

	/**
	 * Handles the continuous drag movement and runs on every drag tick
	 * Contains the main snapping logic
	 */
	public drag(event: HammerEvent): void {
		if (!this.canDrag()) {
			return;
		}

		(this._dragAtEdge as DragAtEdge).update(event.center);
		let deltaX: number = (event.deltaX as number) / this.canvasAngularLand.cameraService.camera.scale.x;
		let x: number =
			this._timelineItem.data.dragDirection === PeriodDragDirection.Right
				? this._endX + deltaX
				: this._startX + deltaX;
		const snapObject = snapping(x, [this._cachedActiveColumns], settings.snapStickiness, [
			(item: IActiveColumn) => {
				return item.finishX;
			},
		]);

		if (!snapObject || !snapObject.item) {
			return;
		}

		if (!this._cachedActiveLine?.view.activeLineDate) {
			return;
		}

		this._lastSnapColumn = snapObject.item;

		let newDate: string = dateCache.getFormat(this._lastSnapColumn?.activeDay);
		if (newDate > this._cachedActiveLine.view.activeLineDate) {
			newDate = this._cachedActiveLine.view.activeLineDate;
		}

		if (this.isInvalidDrag(deltaX, this.canvasAngularLand.activeColumnService.columnWidth)) {
			return;
		}

		if (this._timelineItem.data.dragDirection === PeriodDragDirection.Right) {
			this.canvasAngularLand.promisePeriodService.setEndDate(this._timelineItem.data.period.$id, newDate);
		}

		if (this._timelineItem.data.dragDirection === PeriodDragDirection.Left) {
			this.canvasAngularLand.promisePeriodService.setStartDate(this._timelineItem.data.period.$id, newDate);
		}
	}

	/**
	 * Handles the end of a drag operation
	 */
	public dragEnd(_: HammerEvent): void {
		if (!this.canDrag()) {
			return;
		}

		(this._dragAtEdge as DragAtEdge).cancel();
		this._dragCleanup.forEach((sub) => sub.unsubscribe());
		this._dragCleanup = [];

		if (!this._lastSnapColumn) {
			return;
		}

		this.canvasAngularLand.promisePeriodService.save(RenderType.Database);
		this.canvasAngularLand.promisePeriodService.switchViewToDatabase();

		analyticsEventsService.promisePeriodDragged(this.canvasAngularLand.planId);
	}
	/**
	 * Cleanup method called when the component is destroyed
	 * Ensures all subscriptions and resources are properly disposed
	 */
	public override destroy(opts?: IDestroyOptions) {
		super.destroy(opts);
		this._shutdownSubject$?.next();
		this._shutdownSubject$?.complete();
		this._binder?.destroy();
		this._dragAtEdge = null;
		this._cachedActiveLine = null;
		this._cachedActiveColumns = null;
		this._binder = null;
		this._shutdownSubject$ = null;
		this._cachedPromisePeriodList = null;
	}
	/**
	 * Helper method to determine if we are allowed to drag the promise period.
	 * @private
	 */
	private canDrag(): boolean {
		return (
			this._cachedActiveLine !== null &&
			this._cachedPromisePeriodList !== null &&
			this._cachedActiveColumns !== null &&
			this.canvasAngularLand.planAccessCheck.isAdminSync &&
			!this.canvasAngularLand.planAccessCheck.hasRestrictedAccessSync
		);
	}
	/**
	 * Helper method to determine if the current drag is invalid or not. This check ensures that we don't cross our
	 * start/end dates regardless of the direction being dragged
	 * @param delta The horizontal X change
	 * @param columnWidth the width of the column
	 * @private
	 */
	private isInvalidDrag(delta: number, columnWidth: number): boolean {
		const direction: PeriodDragDirection = this._timelineItem.data.dragDirection as PeriodDragDirection;
		return (
			(direction === PeriodDragDirection.Right && this._endX + delta < this._startX + columnWidth) ||
			(direction === PeriodDragDirection.Left && this._startX + delta > this._endX - columnWidth)
		);
	}
}
